/*
 * Copyright (c) 2020.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Nikita Tropin <ntropin@yandex.ru>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {CdkDragDrop} from '@angular/cdk/drag-drop';
import {ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output, ViewChild} from '@angular/core';
import {
  BaseComponent,
  FileService,
  FormatSizePipe,
  ImagePreviewComponent,
  ImagePreviewPicture,
  ImageUploadConfig,
  InputFile,
  Picture,
  Product,
  SortingPosition
} from 'compob2b-libs';
import {concatMap, from, Observable, Subscription} from 'rxjs';
import {catchError, finalize, take, takeUntil, tap} from 'rxjs/operators';
import {translations} from '../../../../environments/translations.ru';
import {CrudEvent} from '../../interfaces/crud-event';

@Component({
  selector: 'app-additional-pictures',
  templateUrl: './additional-pictures.component.html',
  styleUrls: ['./additional-pictures.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [FormatSizePipe],
  standalone: false
})
export class AdditionalPicturesComponent extends BaseComponent implements OnInit {
  @Input() model: Product;
  @Input() uri: string;
  @Output() removeAdditionalPictureEvent = new EventEmitter<Picture>(true);
  @Output() loadingEvent = new EventEmitter<CrudEvent>();
  @Output() pictureEvent = new EventEmitter<CrudEvent>();
  @Output() pictureChangeEvent = new EventEmitter<CrudEvent>();
  @ViewChild(ImagePreviewComponent) preview: ImagePreviewComponent;

  showUploadForm = false;
  imageUploadConfig: ImageUploadConfig = {
    dangerText: '',
    maxFileSize: 5 * 1024 * 1024,
    allowedMimeType: ['image/png', 'image/jpg', 'image/jpeg', 'image/gif']
  };
  formatSize = inject(FormatSizePipe);
  toEmit = false;

  private subscription: Subscription;

  constructor(protected readonly fileService: FileService
  ) {
    super();
  }

  ngOnInit() {
    this.compoApiService.get<ImageUploadConfig>('/image-upload-config')
      .pipe(take(1), catchError(err => this.handleError(err)))
      .subscribe(data => {
        this.imageUploadConfig.dangerText = data.data.dangerText || this.imageUploadConfig.dangerText;
        this.imageUploadConfig.maxFileSize = data.data.maxFileSize || this.imageUploadConfig.maxFileSize;
        this.imageUploadConfig.allowedMimeType = data.data.allowedMimeType || this.imageUploadConfig.allowedMimeType;

        if (!this.imageUploadConfig.dangerText) {
          this.imageUploadConfig.dangerText = 'Максимальный размер файла ' + this.formatSize.transform(this.imageUploadConfig.maxFileSize);
        }

        this.cdr.detectChanges()
      });
  }

  showUpdateMessage() {
    this.successToast(translations.updated);
    this.loading = false;
    this.cdr.detectChanges();
  }

  drop(event: CdkDragDrop<any>) {
    const prevIndex = event.previousContainer.data.index;
    const targetIndex = event.container.data.index;

    this.widgetsMove(this.model.pictures, prevIndex, targetIndex);

    this.model.pictures.forEach((value, idx) => value.pos = idx);
    this.updatePicturesPositions();
  }

  widgetsMove(arr: any, fromIndex: number, toIndex: number) {
    const element = arr[fromIndex];
    arr.splice(fromIndex, 1);
    arr.splice(toIndex, 0, element);
    return arr;
  }

  updatePicturesPositions() {
    const uri = this.uri + '/pictures';
    const sortingPositions: SortingPosition[] = [];
    this.model.pictures.forEach(picture => sortingPositions.push({id: picture.id, pos: picture.pos}));
    this.compoApiService.post(uri, sortingPositions).pipe(take(1), catchError(err => this.handleError(err))).subscribe();
  }

  removeAdditionalPicture(picture: Picture) {
    this.confirm(translations.confirmDelete + ' ' + translations.image + '?', new Observable(() => {
      this.removePicture(picture);
    }));
  }

  removePicture(picture: Picture) {
    this.loading = true;

    const uri = this.uri + '/picture/' + picture.id;
    this.compoApiService.delete<Picture>(uri)
      .pipe(
        take(1),
        catchError(err => this.handleError(err)),
        tap(_ => {
          const index = this.model.pictures.findIndex(el => el.id === picture.id);
          this.model.pictures.splice(index, 1);
          this.showUpdateMessage();
        }),
      ).subscribe();
  }

  openPicture(index: number) {
    this.preview.show(index, this.model.pictures
      .map(item => {
        return {
          src: this.fileService.getImageUri(item),
          picture: item
        } as ImagePreviewPicture;
      }));
  }

  getImageUri(picture: Picture) {
    return this.fileService.getImageUri(picture, 200) ?? this.noPhotoSrc;
  }

  handleImageError(event: Event) {
    const img = event.target as HTMLImageElement;
    img.className += ' error';
    img.onerror = null; // чтобы избежать бесконечного цикла если дефолтное тоже не загрузится
  }

  onUpload(files: InputFile[]) {
    this.loading = true;

    this.loadingEvent.emit(CrudEvent.UPLOADING);

    this.subscription = from(files)
      .pipe(
        catchError(error => this.handleError(error)),
        takeUntil(this.unsubscribe),
        concatMap((uploadingFile, index) => this.uploadFile(uploadingFile, index)),
        finalize(() => this.finishUpload()),
      ).subscribe(resp => {
        this.successLoadingPicture(resp);
      });
  }

  finishUpload(): void {
    this.loadingEvent.emit(CrudEvent.UPLOADING_DONE);
    this.showUpdateMessage();
    this.subscription.unsubscribe();
  }

  uploadFile(uploadingFile, index: number): Observable<any> {
    console.warn('uploadFile', uploadingFile, index);
    const isMain = !this.model?.pictures?.length && index === 0;

    if (isMain) {
      this.toEmit = true;
    }

    return this.compoApiService.postForm<InputFile, string>(
      isMain ? this.uri + '/upload-main-picture' : this.uri + '/upload-picture',
      {file: uploadingFile.file}
    );
  }

  successLoadingPicture(res) {
    console.warn('successLoadingPicture', res);

    if (this.model.pictures.findIndex(item => item.id === res.data.id) === -1) {
      this.model.pictures.push(res.data);
    }
    if (!this.model?.picture && this.toEmit === true) {
      this.pictureEvent.emit(CrudEvent.UPDATE_PICTURE);
    }

    this.cdr.detectChanges();
  }

}
